/** @format */

import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ContentEditor from "../contentEditor/contentEditor";
import DeleteModal from "../deleteModal/deleteModal";
import FeatureFlag from "../featureFlag/FeatureFlag";
import PageHeader from "../pageHeader/PageHeader";
import Sidebar from "../sidebar/Sidebar";
import { Resource, UserResource } from "./types";
import { selectShowSidebar } from "../sidebar/sidebarSlice";
import { useLocale } from "../i18n/hooks";
import { setLessonsProgress } from "../lessonProgress/processSlice";
import { selectIsEditable } from "../editable/editableSlice";
import "./ResourceUI.scss";

type Props = {
  resource: Resource | UserResource;
};

const ResourceUI: React.FC<Props> = ({ resource }) => {
  const locale = useLocale();
  const dispatch = useAppDispatch();
  const [theme] = useState(
    resource.Qualification || resource.Object.themeLevel || "gcse",
  );
  const showSidebar = useAppSelector(selectShowSidebar());
  const isEditable = useAppSelector(selectIsEditable());

  useEffect(() => {
    const lessonProgress = window.localStorage.getItem("lessonProgress");
    if (lessonProgress) {
      dispatch(setLessonsProgress(JSON.parse(lessonProgress)));
    }
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", theme);
  }, [resource, theme]);

  return (
    <div className="container-fluid position-relative" id="main-container">
      <div className="row">
        <>
          <Sidebar resource={resource.Object} />
          <div
            data-testid="resource-wrapper"
            className={`wrapper position-relative ${
              showSidebar ? "overlay-lg" : ""
            } col p-0`}
          >
            <div
              className={`page-container ${
                isEditable ? "isEditable" : ""
              } overflow-auto p-0 ${theme}`}
            >
              <div className="header page-header px-3 px-md-5 pt-3 pt-sm-5">
                <PageHeader
                  resourceTitle={resource.Object?.resourceTitle[locale]}
                  subject={resource.Subject}
                  themeLevel={theme}
                />
              </div>
              <div className="page-content px-1 px-sm-3 px-md-5">
                <Outlet context={resource} />
              </div>
            </div>
          </div>
          <FeatureFlag flag={["editable", "contenteditor", "dev"]}>
            <ContentEditor resource={resource as UserResource} />
            <DeleteModal resource={resource} />
          </FeatureFlag>
        </>
      </div>
    </div>
  );
};

export default ResourceUI;
